<div class="ai-settings-page">
    <div *ngIf="isLoading" class="ai-settings-loading">
        {{'aumico_ai_settings_loading' | i18next}}
    </div>

    <ng-container *ngIf="!isLoading && settings">
        <!-- ── Hero (aumico promo pattern, 2FA-style) ────────────────── -->
        <div class="ai-settings-hero">
            <div class="ai-settings-hero-illustration">
                <span
                    class="ai-settings-hero-illustration-status"
                    [class.is-active]="isMasterEffectivelyEnabled"
                    [class.is-inactive]="!isMasterEffectivelyEnabled"
                >
                    {{ (isMasterEffectivelyEnabled ? 'aumico_ai_settings_status_active' : 'aumico_ai_settings_status_inactive') | i18next }}
                </span>
                <div>
                    <h3 class="ai-settings-hero-illustration-headline">
                        {{'aumico_ai_settings_hero_headline' | i18next}}
                    </h3>
                    <p class="ai-settings-hero-illustration-sub">
                        {{'aumico_ai_settings_hero_sub' | i18next}}
                    </p>
                </div>
            </div>
            <div class="ai-settings-hero-content">
                <h2 class="ai-settings-hero-title">{{'aumico_ai_settings_title' | i18next}}</h2>
                <p class="ai-settings-hero-desc">{{'aumico_ai_settings_description' | i18next}}</p>
                <p class="ai-settings-hero-desc">{{'aumico_ai_settings_master_description' | i18next}}</p>

                <!-- Master toggle inline in the hero (no duplicate "Aktivieren" row) -->
                <div class="ai-settings-hero-toggle" [class.is-active]="isMasterEffectivelyEnabled">
                    <div class="ai-settings-hero-toggle-text">
                        <span class="ai-settings-hero-toggle-label">{{'aumico_ai_settings_master_title' | i18next}}</span>
                        <span class="ai-settings-hero-toggle-sub">{{'aumico_ai_settings_master_short_description' | i18next}}</span>
                    </div>
                    <ham-toggle
                        *ngIf="masterToggleVisible"
                        data-testid="ai-master-toggle"
                        [checked]="isMasterEffectivelyEnabled"
                        [disabled]="masterToggleLoading"
                        (changed)="onMasterToggleChange($event)"
                    ></ham-toggle>
                </div>
            </div>
        </div>

        <!-- ── Usage stats ───────────────────────────────────────────── -->
        <div class="ai-settings-section">
            <h3 class="ai-settings-section-title">{{'aumico_ai_settings_usage_title' | i18next}}</h3>
            <div class="ai-settings-stats">
                <div class="ai-settings-stat" [class.is-warn]="dailyUsagePct >= 80">
                    <div class="ai-settings-stat-header">
                        <span class="ai-settings-stat-label">{{'aumico_ai_settings_daily_usage' | i18next}}</span>
                        <span class="ai-settings-stat-pct" *ngIf="settings.dailyTokenLimit !== null">
                            {{ dailyUsagePct }}%
                        </span>
                    </div>
                    <div class="ai-settings-stat-value">
                        <span class="ai-settings-stat-current">{{ formatNumber(settings.currentDailyTokens) }}</span>
                        <span *ngIf="settings.dailyTokenLimit !== null" class="ai-settings-stat-limit">
                            / {{ formatNumber(settings.dailyTokenLimit) }}
                        </span>
                        <span class="ai-settings-stat-unit">{{'aumico_ai_settings_tokens' | i18next}}</span>
                    </div>
                    <div class="ai-settings-progress" *ngIf="settings.dailyTokenLimit !== null">
                        <div class="ai-settings-progress-bar" [style.width.%]="dailyUsagePct"></div>
                    </div>
                    <div class="ai-settings-stat-footer">{{'aumico_ai_settings_resets_daily' | i18next}}</div>
                </div>

                <div class="ai-settings-stat" [class.is-warn]="monthlyUsagePct >= 80">
                    <div class="ai-settings-stat-header">
                        <span class="ai-settings-stat-label">{{'aumico_ai_settings_monthly_usage' | i18next}}</span>
                        <span class="ai-settings-stat-pct" *ngIf="settings.monthlyTokenLimit !== null">
                            {{ monthlyUsagePct }}%
                        </span>
                    </div>
                    <div class="ai-settings-stat-value">
                        <span class="ai-settings-stat-current">{{ formatNumber(settings.currentMonthlyTokens) }}</span>
                        <span *ngIf="settings.monthlyTokenLimit !== null" class="ai-settings-stat-limit">
                            / {{ formatNumber(settings.monthlyTokenLimit) }}
                        </span>
                        <span class="ai-settings-stat-unit">{{'aumico_ai_settings_tokens' | i18next}}</span>
                    </div>
                    <div class="ai-settings-progress" *ngIf="settings.monthlyTokenLimit !== null">
                        <div class="ai-settings-progress-bar" [style.width.%]="monthlyUsagePct"></div>
                    </div>
                    <div class="ai-settings-stat-footer">{{'aumico_ai_settings_resets_monthly' | i18next}}</div>
                </div>
            </div>

            <ul class="ai-settings-info-note">
                <li>{{'aumico_ai_settings_info_blocked' | i18next}}</li>
                <li>{{'aumico_ai_settings_info_agb' | i18next}}</li>
            </ul>
        </div>

        <!-- ── Available agents ──────────────────────────────────────── -->
        <div class="ai-settings-section">
            <div class="ai-settings-agents-heading">
                <h3 class="ai-settings-section-title">{{'aumico_ai_settings_agents_title' | i18next}}</h3>
                <span class="ai-settings-wip-pill">{{'aumico_ai_settings_wip_label' | i18next}}</span>
            </div>

            <div class="ai-settings-wip-banner">
                <p class="ai-settings-wip-headline">{{'aumico_ai_settings_wip_headline' | i18next}}</p>
                <p class="ai-settings-wip-text">{{'aumico_ai_settings_wip_text' | i18next}}</p>
            </div>

            <div class="ai-settings-agent-grid">
                <div
                    class="ai-settings-agent-card"
                    *ngFor="let card of featureCards"
                    data-testid="ai-feature-card"
                    [attr.data-feature]="card.id"
                >
                    <div class="ai-settings-agent-card-top">
                        <span class="ai-settings-agent-card-glyph">
                            <lucide-icon [name]="card.icon" [size]="18" aria-hidden="true"></lucide-icon>
                        </span>
                        <span class="ai-settings-agent-card-status">{{'aumico_ai_settings_agent_available' | i18next}}</span>
                    </div>
                    <div class="ai-settings-agent-card-body">
                        <span class="ai-settings-agent-card-title">{{ card.titleKey | i18next }}</span>
                        <span class="ai-settings-agent-card-desc">{{ card.textKey | i18next }}</span>
                    </div>
                </div>

                <!-- Roadmap placeholder so the section never feels empty -->
                <div class="ai-settings-agent-card is-coming-soon">
                    <div class="ai-settings-agent-card-top">
                        <span class="ai-settings-agent-card-glyph is-soon">
                            <lucide-icon name="plus" [size]="18" aria-hidden="true"></lucide-icon>
                        </span>
                        <span class="ai-settings-agent-card-status is-soon">{{'aumico_ai_settings_agent_soon' | i18next}}</span>
                    </div>
                    <div class="ai-settings-agent-card-body">
                        <span class="ai-settings-agent-card-title">{{'aumico_ai_settings_agent_more_title' | i18next}}</span>
                        <span class="ai-settings-agent-card-desc">{{'aumico_ai_settings_agent_more_text' | i18next}}</span>
                    </div>
                </div>
            </div>
        </div>
    </ng-container>

    <div *ngIf="!isLoading && !settings" class="ai-settings-error">
        {{'aumico_ai_settings_load_error' | i18next}}
    </div>
</div>

<!-- Master AGB acceptance modal -->
<ham-modal
    *ngIf="showAcceptanceModal && settings"
    [title]="('aumico_ai_settings_master_title' | i18next)"
    showCloseIcon="true"
    showCancelButton="true"
    variant="grayfooter"
    cancelText="{{'aumico_ai_settings_acceptance_decline' | i18next}}"
    saveText="{{'aumico_ai_settings_acceptance_accept' | i18next}}"
    (cancel)="cancelAcceptance()"
    (save)="confirmAcceptance()"
>
    <div class="ai-settings-acceptance">
        <p class="ai-settings-acceptance-intro">
            {{'aumico_ai_settings_acceptance_intro' | i18next}}
        </p>
        <div class="ai-settings-acceptance-text">
            {{ pickText(settings.masterAgb.text) || ('aumico_ai_settings_master_agb_empty' | i18next) }}
        </div>
        <p class="ai-settings-acceptance-disclaimer">
            {{'aumico_ai_settings_acceptance_disclaimer' | i18next}}
        </p>
    </div>
</ham-modal>
